import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setProductData } from '../store/productSlice';
import { setAdminData } from '../store/adminSlice';
import { setCalendarData } from '../store/calendarSlice';
import { setTransactions } from '../store/salesSlice';
import { PW_Products_API, PW_Stores_API, PW_Users_API, PW_Calendar_API, PW_Transactions_List_API } from '../Server/ApiV2';
import Spinner from './Spinner';

export default function Dashboard() {
  const dispatch = useDispatch();
  const { products, productTypes } = useSelector(s => s.product);
  const { schedules } = useSelector(s => s.calendar);
  const { stores, users } = useSelector(s => s.admin);
  const { transactions } = useSelector(s => s.sales);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [prodRes, storeRes, userRes, calRes, txnRes] = await Promise.all([
        PW_Products_API(), PW_Stores_API(), PW_Users_API(), PW_Calendar_API(), PW_Transactions_List_API(),
      ]);
      if (cancelled) return;
      if (prodRes?.data?.success) dispatch(setProductData(prodRes.data.data));
      if (storeRes?.data?.success) dispatch(setAdminData({ stores: storeRes.data.data }));
      if (userRes?.data?.success) {
        const u = userRes.data.data;
        dispatch(setAdminData({ users: u.users, userStoreRights: u.userStoreRights, userTypes: u.userTypes }));
      }
      if (calRes?.data?.success) dispatch(setCalendarData(calRes.data.data));
      if (txnRes?.data?.success) dispatch(setTransactions(txnRes.data.data));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  const [selStores, setSelStores] = useState([]);
  const [selUsers, setSelUsers] = useState([]);
  const [selProducts, setSelProducts] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [storeOpen, setStoreOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);

  const [applied, setApplied] = useState({ stores: [], users: [], products: [], dateFrom: '', dateTo: '', timeFrom: '', timeTo: '' });

  const allStores = useMemo(() => stores.map(s => s.name).sort(), [stores]);
  const allUsers = useMemo(() => users.map(u => u.name).sort(), [users]);
  const allProducts = useMemo(() => products.map(p => p.name).sort(), [products]);

  const filtered = useMemo(() => {
    let list = transactions;
    if (applied.stores.length) list = list.filter(t => applied.stores.includes(t.storeName));
    if (applied.users.length) list = list.filter(t => applied.users.includes(t.userName));
    if (applied.products.length) list = list.filter(t => applied.products.includes(t.productName));
    if (applied.dateFrom) list = list.filter(t => t.timestamp >= applied.dateFrom);
    if (applied.dateTo) list = list.filter(t => t.timestamp <= applied.dateTo + 'T23:59:59');
    if (applied.timeFrom) list = list.filter(t => (t.timestamp.split('T')[1] || '').substring(0, 5) >= applied.timeFrom);
    if (applied.timeTo) list = list.filter(t => (t.timestamp.split('T')[1] || '').substring(0, 5) <= applied.timeTo);
    return list;
  }, [transactions, applied]);

  const toggleChip = (val, sel, setSel) => setSel(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const toggleAll = (all, sel, setSel) => setSel(sel.length === all.length ? [] : [...all]);
  const [filterError, setFilterError] = useState('');
  const handleApply = () => {
    if (!selStores.length && !selUsers.length && !selProducts.length && !dateFrom && !dateTo && !timeFrom && !timeTo) {
      setFilterError('Select at least one filter');
      return;
    }
    if ((dateFrom && !dateTo) || (!dateFrom && dateTo)) {
      setFilterError('Both From Date and To Date are required');
      return;
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setFilterError('From Date cannot be greater than To Date');
      return;
    }
    if ((timeFrom && !timeTo) || (!timeFrom && timeTo)) {
      setFilterError('Both From Time and To Time are required');
      return;
    }
    if (timeFrom && timeTo && timeFrom > timeTo) {
      setFilterError('From Time cannot be greater than To Time');
      return;
    }
    setFilterError('');
    setApplied({ stores: selStores, users: selUsers, products: selProducts, dateFrom, dateTo, timeFrom, timeTo });
  };
  const clearFilters = () => { setSelStores([]); setSelUsers([]); setSelProducts([]); setDateFrom(''); setDateTo(''); setTimeFrom(''); setTimeTo(''); setFilterError(''); setApplied({ stores: [], users: [], products: [], dateFrom: '', dateTo: '', timeFrom: '', timeTo: '' }); };
  const hasFilters = applied.stores.length > 0 || applied.users.length > 0 || applied.products.length > 0 || applied.dateFrom || applied.dateTo || applied.timeFrom || applied.timeTo;
  const pendingDiff = JSON.stringify(selStores) !== JSON.stringify(applied.stores) || JSON.stringify(selUsers) !== JSON.stringify(applied.users) || JSON.stringify(selProducts) !== JSON.stringify(applied.products) || dateFrom !== applied.dateFrom || dateTo !== applied.dateTo || timeFrom !== applied.timeFrom || timeTo !== applied.timeTo;
  const storeRef = useRef(null);
  const userRef = useRef(null);
  const productRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (storeRef.current && !storeRef.current.contains(e.target)) setStoreOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
      if (productRef.current && !productRef.current.contains(e.target)) setProductOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const wins = filtered.filter(t => t.isWinner);
  const totalPrize = wins.reduce((s, t) => s + (t.prize || 0), 0);
  const totalBonus = wins.reduce((s, t) => s + (t.bonusAmount || 0), 0);
  const revenue = filtered.reduce((s, t) => s + (products.find(p => p.id === t.productId)?.rsp || 0), 0);

  const pulseData = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    const padL = 50, W = 850, chartW = W - padL, H = 220, padY = 14;
    if (!sorted.length) return { path: '', dots: [], W, H, padL, ticks: [] };
    const maxPrize = Math.max(...sorted.map(t => t.prize || 0), 1);
    const stepX = sorted.length > 1 ? chartW / (sorted.length - 1) : chartW / 2;
    const scaleY = (v) => H - padY - ((v / maxPrize) * (H - padY * 2));
    const baseY = scaleY(0);
    const dots = [];
    const segments = [];
    sorted.forEach((t, i) => {
      const x = padL + (sorted.length > 1 ? i * stepX : chartW / 2);
      if (t.isWinner) {
        const peakY = scaleY(t.prize);
        segments.push(`L${x - stepX * 0.15},${baseY}`);
        segments.push(`L${x - stepX * 0.05},${baseY}`);
        segments.push(`L${x},${peakY}`);
        segments.push(`L${x + stepX * 0.05},${baseY}`);
        segments.push(`L${x + stepX * 0.15},${baseY}`);
        dots.push({ x, y: peakY, prize: t.prize, time: new Date(t.timestamp).toLocaleTimeString(), product: t.productName, type: t.typeId });
      } else {
        segments.push(`L${x},${baseY}`);
      }
    });
    const path = `M${padL},${baseY} ${segments.join(' ')}`;
    const tickCount = 4;
    const ticks = [];
    for (let i = 0; i <= tickCount; i++) {
      const val = Math.round((maxPrize / tickCount) * i);
      ticks.push({ val, y: scaleY(val), label: val >= 1000 ? `${val / 1000}K` : String(val) });
    }
    return { path, dots, W, H, baseY, padL, ticks };
  }, [filtered]);

  const [hoveredDot, setHoveredDot] = useState(null);

  const byType = {};
  filtered.forEach(t => {
    const key = `${t.productName || 'Unknown'}|${t.typeId || '?'}`;
    if (!byType[key]) byType[key] = { product: t.productName, productId: t.productId, typeId: t.typeId, wins: [], total: 0 };
    byType[key].total++;
    if (t.isWinner) byType[key].wins.push(t);
  });
  const typeCharts = Object.values(byType)
    .map(g => {
      const groups = {};
      g.wins.forEach(t => { groups[t.prize] = (groups[t.prize] || 0) + 1; });
      const bars = Object.entries(groups).map(([p, q]) => ({ price: Number(p), qty: q })).sort((a, b) => a.price - b.price);
      const rsp = products.find(p => p.id === g.productId)?.rsp || 0;
      const typeRevenue = g.total * rsp;
      const typePrize = g.wins.reduce((s, t) => s + t.prize, 0);
      return { ...g, bars, maxQty: Math.max(...bars.map(b => b.qty), 1), totalPrize: typePrize, revenue: typeRevenue, profit: typeRevenue - typePrize };
    });

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Dashboard</h2>
          <div className="page-header-sub">Overview of operations</div>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card purple">
          <div className="stat-label">Products</div>
          <div className="stat-value purple">{products.length}</div>
          <div className="stat-sub">{products.filter(p => p.status === 'Active').length} active</div>
        </div>
        <div className="stat-card gold">
          <div className="stat-label">Schedules</div>
          <div className="stat-value gold">{schedules.length}</div>
          <div className="stat-sub">{schedules.filter(s => s.status === 'Active').length} active</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Product Types</div>
          <div className="stat-value green">{productTypes.length}</div>
          <div className="stat-sub">{productTypes.filter(t => t.status === 'Active').length} active</div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">Stores</div>
          <div className="stat-value blue">{stores.length}</div>
          <div className="stat-sub">{stores.filter(s => s.status === 'Active').length} active</div>
        </div>
      </div>


      <div className="card mb-24">
          <div className="card-title">Sales Analytics ({filtered.length}{hasFilters ? ` of ${transactions.length}` : ''})</div>
          <div className="card-body">
            <div className="dash-filters">
              <div className="dash-filter-group" ref={storeRef}>
                <div className="text-sm text-muted mb-4">Store</div>
                <div className="dash-csv-select" onClick={() => setStoreOpen(!storeOpen)}>
                  <span>{selStores.length ? selStores.join(', ') : 'All Stores'}</span>
                  <span className="dash-csv-arrow">{storeOpen ? '▲' : '▼'}</span>
                </div>
                {storeOpen && (
                  <div className="dash-csv-dropdown">
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0dce8', padding: '6px 8px' }}>
                      <label className="dash-csv-option" style={{ fontWeight: 600, padding: 0 }}>
                        <input type="checkbox" checked={selStores.length === allStores.length && allStores.length > 0} onChange={() => toggleAll(allStores, selStores, setSelStores)} />
                        <span>Select All</span>
                      </label>
                      {selStores.length > 0 && <span style={{ color: '#e74c3c', cursor: 'pointer', fontSize: 12, alignSelf: 'center' }} onClick={() => setSelStores([])}>Clear All</span>}
                    </div>
                    {allStores.map(s => (
                      <label key={s} className="dash-csv-option">
                        <input type="checkbox" checked={selStores.includes(s)} onChange={() => toggleChip(s, selStores, setSelStores)} />
                        <span>{s}</span>
                      </label>
                    ))}
                    {allStores.length === 0 && <div className="text-sm text-muted" style={{ padding: 8 }}>No stores</div>}
                  </div>
                )}
              </div>
              <div className="dash-filter-group" ref={userRef}>
                <div className="text-sm text-muted mb-4">User</div>
                <div className="dash-csv-select" onClick={() => setUserOpen(!userOpen)}>
                  <span>{selUsers.length ? selUsers.join(', ') : 'All Users'}</span>
                  <span className="dash-csv-arrow">{userOpen ? '▲' : '▼'}</span>
                </div>
                {userOpen && (
                  <div className="dash-csv-dropdown">
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0dce8', padding: '6px 8px' }}>
                      <label className="dash-csv-option" style={{ fontWeight: 600, padding: 0 }}>
                        <input type="checkbox" checked={selUsers.length === allUsers.length && allUsers.length > 0} onChange={() => toggleAll(allUsers, selUsers, setSelUsers)} />
                        <span>Select All</span>
                      </label>
                      {selUsers.length > 0 && <span style={{ color: '#e74c3c', cursor: 'pointer', fontSize: 12, alignSelf: 'center' }} onClick={() => setSelUsers([])}>Clear All</span>}
                    </div>
                    {allUsers.map(u => (
                      <label key={u} className="dash-csv-option">
                        <input type="checkbox" checked={selUsers.includes(u)} onChange={() => toggleChip(u, selUsers, setSelUsers)} />
                        <span>{u}</span>
                      </label>
                    ))}
                    {allUsers.length === 0 && <div className="text-sm text-muted" style={{ padding: 8 }}>No users</div>}
                  </div>
                )}
              </div>
              <div className="dash-filter-group" ref={productRef}>
                <div className="text-sm text-muted mb-4">Product</div>
                <div className="dash-csv-select" onClick={() => setProductOpen(!productOpen)}>
                  <span>{selProducts.length ? selProducts.join(', ') : 'All Products'}</span>
                  <span className="dash-csv-arrow">{productOpen ? '▲' : '▼'}</span>
                </div>
                {productOpen && (
                  <div className="dash-csv-dropdown">
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0dce8', padding: '6px 8px' }}>
                      <label className="dash-csv-option" style={{ fontWeight: 600, padding: 0 }}>
                        <input type="checkbox" checked={selProducts.length === allProducts.length && allProducts.length > 0} onChange={() => toggleAll(allProducts, selProducts, setSelProducts)} />
                        <span>Select All</span>
                      </label>
                      {selProducts.length > 0 && <span style={{ color: '#e74c3c', cursor: 'pointer', fontSize: 12, alignSelf: 'center' }} onClick={() => setSelProducts([])}>Clear All</span>}
                    </div>
                    {allProducts.map(p => (
                      <label key={p} className="dash-csv-option">
                        <input type="checkbox" checked={selProducts.includes(p)} onChange={() => toggleChip(p, selProducts, setSelProducts)} />
                        <span>{p}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="dash-filter-group">
                <div className="text-sm text-muted mb-4">Date Range</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="date" className="form-input-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                  <span className="text-sm text-muted">to</span>
                  <input type="date" className="form-input-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                </div>
              </div>
              <div className="dash-filter-group">
                <div className="text-sm text-muted mb-4">Time Range</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="time" className="form-input-sm" value={timeFrom} onChange={e => setTimeFrom(e.target.value)} />
                  <span className="text-sm text-muted">to</span>
                  <input type="time" className="form-input-sm" value={timeTo} onChange={e => setTimeTo(e.target.value)} />
                </div>
              </div>
              <div className="dash-filter-actions">
                <button className="btn btn-sm btn-success" onClick={handleApply}>Apply</button>
                {hasFilters && <button className="btn btn-sm btn-outline" onClick={clearFilters}>Clear</button>}
              </div>
              {filterError && <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 8, width: '100%' }}>{filterError}</div>}
            </div>

            <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
              <div className="stat-box"><div className="stat-value">{filtered.length}</div><div className="stat-label">Total Coupons</div></div>
              <div className="stat-box"><div className="stat-value" style={{ color: '#00895e' }}>{wins.length}</div><div className="stat-label">Winners</div></div>
              <div className="stat-box"><div className="stat-value">{filtered.length ? (wins.length / filtered.length * 100).toFixed(1) : 0}%</div><div className="stat-label">Win Rate</div></div>
              <div className="stat-box"><div className="stat-value">{revenue.toLocaleString()}</div><div className="stat-label">Revenue</div></div>
              <div className="stat-box"><div className="stat-value" style={{ color: '#c9a227' }}>{totalPrize.toLocaleString()}</div><div className="stat-label">Prizes Paid</div></div>
              {totalBonus > 0 && <div className="stat-box"><div className="stat-value" style={{ color: '#c9a227' }}>{totalBonus.toLocaleString()}</div><div className="stat-label">Bonus Paid</div></div>}
              <div className="stat-box"><div className="stat-value" style={{ color: revenue - totalPrize >= 0 ? '#00895e' : '#e74c3c' }}>{(revenue - totalPrize).toLocaleString()}</div><div className="stat-label">Profit / Loss</div></div>
            </div>

            {filtered.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div className="text-sm fw-600 mb-8" style={{ color: '#2d1b69' }}>Transaction Pulse</div>
                <div className="pulse-chart-wrap">
                  <svg viewBox={`0 0 ${pulseData.W} ${pulseData.H}`} preserveAspectRatio="none" className="pulse-svg">
                    {pulseData.ticks?.map((tk, i) => (
                      <g key={i}>
                        <line x1={pulseData.padL} y1={tk.y} x2={pulseData.W} y2={tk.y} stroke="#e0dce8" strokeWidth="0.5" strokeDasharray={tk.val === 0 ? 'none' : '3,3'} />
                        <text x={pulseData.padL - 6} y={tk.y + 3.5} textAnchor="end" fontSize="9" fill="#999">{tk.label}</text>
                      </g>
                    ))}
                    <path d={pulseData.path} fill="none" stroke="#2d1b69" strokeWidth="1.5" strokeLinejoin="round" />
                    {pulseData.dots.map((d, i) => (
                      <circle key={i} cx={d.x} cy={d.y} r={hoveredDot === i ? 5 : 3} fill="#c9a227" stroke="#fff" strokeWidth="1"
                        onMouseEnter={() => setHoveredDot(i)} onMouseLeave={() => setHoveredDot(null)} style={{ cursor: 'pointer' }} />
                    ))}
                  </svg>
                  {hoveredDot !== null && pulseData.dots[hoveredDot] && (
                    <div className="pulse-tooltip" style={{ left: `calc(${(pulseData.dots[hoveredDot].x / pulseData.W) * 100}%)` }}>
                      <div className="fw-600" style={{ color: '#c9a227' }}>{pulseData.dots[hoveredDot].prize.toLocaleString()}</div>
                      <div className="text-muted">{pulseData.dots[hoveredDot].product} · {pulseData.dots[hoveredDot].type}</div>
                      <div className="text-muted">{pulseData.dots[hoveredDot].time}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {typeCharts.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div className="text-sm fw-600 mb-8" style={{ color: '#2d1b69' }}>Prize Distribution (Cards × Price)</div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  {typeCharts.map(g => (
                    <div key={`${g.product}-${g.typeId}`} className="prize-type-block">
                      <div className="prize-type-header">
                        <span className="fw-600">{g.product}</span>
                        <span className="chip chip-purple" style={{ fontSize: 10 }}>{g.typeId}</span>
                        <span className="text-sm text-muted">{g.wins.length}W / {g.total} sold</span>
                        <span className="text-sm fw-600" style={{ color: '#c9a227' }}>{g.totalPrize.toLocaleString()}</span>
                      </div>
                      {g.bars.length > 0 ? (
                        <div className="prize-chart">
                          {g.bars.map(b => (
                            <div key={b.price} className="prize-chart-row">
                              <div className="prize-chart-label">{b.price >= 1000 ? `${b.price / 1000}K` : b.price}</div>
                              <div className="prize-chart-bar-wrap">
                                <div className="prize-chart-bar" style={{ width: `${(b.qty / g.maxQty) * 100}%` }}>
                                  <span className="prize-chart-val">{b.qty}</span>
                                </div>
                              </div>
                              <div className="prize-chart-amt">{(b.price * b.qty).toLocaleString()}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-muted" style={{ padding: '8px 0' }}>No winners yet</div>
                      )}
                      <div className="prize-type-footer">
                        <span>Rev: <strong style={{ color: '#2d1b69' }}>{g.revenue.toLocaleString()}</strong></span>
                        <span>Prizes: <strong style={{ color: '#c9a227' }}>{g.totalPrize.toLocaleString()}</strong></span>
                        <span>P/L: <strong style={{ color: g.profit >= 0 ? '#00895e' : '#e74c3c' }}>{g.profit >= 0 ? '+' : ''}{g.profit.toLocaleString()}</strong></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>

      <div className="card">
        <div className="card-title">Recent Activity</div>
        <div className="card-body">
          {transactions.length === 0 ? (
            <div className="text-sm text-muted" style={{ padding: 16 }}>No recent activity yet.</div>
          ) : (
            <ul className="activity-list">
              {transactions.slice(0, 10).map(t => (
                <li key={t.id} className="activity-item">
                  <div className="activity-dot" />
                  <div>
                    <div className="activity-text">
                      <span className="activity-user">{t.isWinner ? 'Winner' : 'Coupon Sold'}</span> — {t.productName} ({t.typeId}) at {t.storeName}{t.isWinner ? ` — Prize ${t.prize}` : ''}
                    </div>
                    <div className="activity-time">
                      {t.userName} &middot; {new Date(t.timestamp).toLocaleString()}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

    </div>
  );
}
